import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { SelectModalListItem } from '@/components/molecules/modal/select-modal';
import { OnClickQuestionProps } from '@/components/molecules/question-list/question-item';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useReportModal, { ReportData } from '@/hooks/useReportModal';
import { tokenState } from '@/recoil/atoms/common/auth';
import { ROUTES } from '@/types/common';
import { EIntegratedSearchTabType, ERequestButtonType, KeywordSearchQuestionVo } from '@/types/search';
import { coreLogger } from '@/utils/logger';

import useGetKeywordQuestionCardInfinite from './useGetKeywordQuestionCardInfinite';

interface Props {
  keyword: string;
  tab: EIntegratedSearchTabType;
}

export interface SearchQuestionViewModel {
  searchedQnaData: InfiniteData<KeywordSearchQuestionVo> | undefined;
  isSearchedQnaLoading: boolean;
  hasSearchedQnaNextPage: boolean | undefined;
  pageName: string;
  showReportModal: boolean;
  reportItems: SelectModalListItem[] | undefined;
  onClickQuestion: ({ sectionName, questionId }: OnClickQuestionProps) => void;
  onShowModal: (e: React.MouseEvent, data: ReportData) => void;
  fetchSearchedQnaNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult>;
  onCloseReportModal: () => void;
  handleClickRequestButton: () => void;
}
const useSearchQuestionViewModel = ({ keyword, tab }: Props): SearchQuestionViewModel => {
  const { moveToPage, pageName } = useCustomRouter();
  const token = useRecoilValue(tokenState);
  const { setShowAuthModal } = useAuthModal();
  const { showReportModal, onShowReportModal, onCloseReportModal, reportItems } = useReportModal();

  const { searchedQnaData, isSearchedQnaLoading, hasSearchedQnaNextPage, fetchSearchedQnaNextPage } =
    useGetKeywordQuestionCardInfinite({
      keyword,
      enabled: tab === EIntegratedSearchTabType.Qna,
    });

  // 질문 클릭 -> 상세페이지 이동
  const onClickQuestion = ({ sectionName, questionId }: OnClickQuestionProps) => {
    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionId}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: {
        questionId,
      },
    });
    coreLogger({
      eventName: 'search_item_click',
      pageName,
      payload: {
        keyword,
        content_id: questionId,
        tab: EIntegratedSearchTabType.Qna,
        sectionName: sectionName ?? '',
      },
    });
  };

  // 질문 신고하기
  const onShowModal = (e: React.MouseEvent, data: ReportData) => {
    e.stopPropagation();
    if (!token) {
      // 로그인 정보가 없는 경우
      setShowAuthModal(true);
      return;
    }
    onShowReportModal(data);
  };

  const handleClickRequestButton = () => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName: pageName,
      payload: {
        position: ERequestButtonType.BOTTOM,
        keyword,
        section_name: 'integrate_search_qna',
      },
    });
    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  return {
    searchedQnaData,
    isSearchedQnaLoading,
    hasSearchedQnaNextPage,
    fetchSearchedQnaNextPage,
    onClickQuestion,
    onShowModal,
    pageName,
    showReportModal,
    onCloseReportModal,
    reportItems,
    handleClickRequestButton,
  };
};

export default useSearchQuestionViewModel;
